import { faPlus, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AudioFilePicker,
  MessageAudioPreview,
  MessageEditor,
  refuseAudioFile,
  uploadErrorNote,
} from "@phone/MessageEditor";
import {
  HOLD_CHECK_IN_DEFAULT_SECONDS,
  HOLD_CHECK_IN_MAX_SECONDS,
  HOLD_CHECK_IN_MIN_SECONDS,
  HOLD_MAX_DEFAULT_SECONDS,
  HOLD_MAX_SECONDS_OPTIONS,
  HOLD_MESSAGE_PURPOSES,
  isValidHoldCheckIn,
  MAX_HOLD_MESSAGES,
  type PhoneHoldMessage,
  type PhoneHoldMessagePurpose,
  type PhoneHoldSetup,
} from "@phone/phoneSetupClient";
import {
  useUpdateOutboundConfig,
  useUpdatePhoneMessage,
  useUploadPhoneMessageAudio,
} from "@phone/phoneSetupQueries";
import {
  Button,
  ConfirmDialog,
  FieldError,
  IconButton,
  Input,
  SegmentedControl,
  Select,
  SettingsCard,
  Tag,
} from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { useState } from "react";

export const HoldSettingsCard = ({ hold }: { hold: PhoneHoldSetup }) => (
  <SettingsCard
    title={
      <span className="inline-flex items-center gap-2">
        Hold
        <Tag tone="emphasis">New</Tag>
      </span>
    }
    subtitle="What a caller hears while your team finishes another call. Putting a caller on hold from a call slip plays this until someone picks up."
    data-testid="phone-hold-card"
  >
    <div className="flex flex-col gap-4">
      <HoldMessages hold={hold} />
      <Divider />
      <HoldMusic hold={hold} />
      <Divider />
      <HoldTiming hold={hold} />
      <HoldSummary hold={hold} />
    </div>
  </SettingsCard>
);

const Divider = () => (
  <hr className="border-0 border-t border-dotted border-rule" />
);

/** A purpose missing from the GET's list. */
const blankHoldMessage = (
  purpose: PhoneHoldMessagePurpose,
): PhoneHoldMessage => ({
  purpose,
  mode: "unset",
  customText: null,
  defaultText: null,
  effectiveText: null,
  audio: null,
});

/** One brian will actually play. */
const isSet = (m: PhoneHoldMessage): boolean =>
  m.purpose === "hold_1" || m.mode === "text" || m.mode === "audio";

/** Adding only opens an editor; the message exists once its text or audio saves. */
const HoldMessages = ({ hold }: { hold: PhoneHoldSetup }) => {
  const [opened, setOpened] = useState<PhoneHoldMessagePurpose[]>([]);
  const [removing, setRemoving] = useState<PhoneHoldMessagePurpose | null>(
    null,
  );
  const updateMessage = useUpdatePhoneMessage();

  const byPurpose = new Map(hold.messages.map((m) => [m.purpose, m]));
  const rows = HOLD_MESSAGE_PURPOSES.map(
    (purpose) => byPurpose.get(purpose) ?? blankHoldMessage(purpose),
  ).filter((m) => isSet(m) || opened.includes(m.purpose));
  const next = HOLD_MESSAGE_PURPOSES.find(
    (purpose) => !rows.some((m) => m.purpose === purpose),
  );

  const remove = (m: PhoneHoldMessage) => {
    if (!isSet(m)) {
      setOpened((o) => o.filter((p) => p !== m.purpose));
      return;
    }
    setRemoving(m.purpose);
  };

  const confirmRemove = () => {
    if (!removing) return;
    const purpose = removing;
    updateMessage.mutate(
      { purpose, input: { mode: "default" } },
      {
        onSuccess: () => setOpened((o) => o.filter((p) => p !== purpose)),
        onSettled: () => setRemoving(null),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {rows.map((m, index) => {
        const first = m.purpose === "hold_1";
        const testIdPrefix = `phone-hold-message-${index + 1}`;
        return (
          <div key={m.purpose} className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-ink">
                {rows.length === 1
                  ? "Hold message"
                  : `Hold message ${index + 1}`}
              </p>
              {!first && (
                <IconButton
                  aria-label={`Remove hold message ${index + 1}`}
                  size="sm"
                  onClick={() => remove(m)}
                  data-testid={`${testIdPrefix}-remove`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              )}
            </div>
            <MessageEditor
              purpose={m.purpose}
              testIdPrefix={testIdPrefix}
              controlLabel={`Hold message ${index + 1} mode`}
              hears="Callers on hold will hear"
              config={m}
              modes={first ? undefined : ["text", "audio"]}
              hideEffective
            />
          </div>
        );
      })}

      {updateMessage.isError && removing === null && (
        <FieldError data-testid="phone-hold-remove-error">
          Couldn't remove that message. Try again.
        </FieldError>
      )}

      <div>
        <Button
          variant="outline"
          size="sm"
          disabled={next === undefined || rows.length >= MAX_HOLD_MESSAGES}
          onClick={() => next && setOpened((o) => [...o, next])}
          data-testid="phone-hold-message-add"
        >
          <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
          Add message
        </Button>
      </div>

      <ConfirmDialog
        open={removing !== null}
        data-testid={
          removing !== null ? "phone-hold-remove-confirm" : undefined
        }
        title="Remove this hold message?"
        confirmLabel="Remove message"
        cancelLabel="Cancel"
        busy={updateMessage.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={confirmRemove}
        confirmTestId="phone-hold-remove-confirm-confirm"
        cancelTestId="phone-hold-remove-confirm-cancel"
      >
        <p className="text-sm text-ink-body">
          Callers on hold will no longer hear it. The other messages keep their
          order.
        </p>
      </ConfirmDialog>
    </div>
  );
};

type MusicSource = "default" | "custom";

const MUSIC_OPTIONS: { value: MusicSource; label: string }[] = [
  { value: "default", label: "Brian default track" },
  { value: "custom", label: "Upload your own" },
];

/** `hold_music` is audio-only; "Upload your own" switches only once the file lands. */
const HoldMusic = ({ hold }: { hold: PhoneHoldSetup }) => {
  const uploadAudio = useUploadPhoneMessageAudio();
  const updateMessage = useUpdatePhoneMessage();
  const [draft, setDraft] = useState<MusicSource | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const saved: MusicSource = hold.music.custom ? "custom" : "default";
  const source = draft ?? saved;
  const picking = uploadAudio.isPending;

  const pick = async (file: File) => {
    const refusal = refuseAudioFile(file);
    setRefused(refusal);
    if (refusal) return;
    try {
      await uploadAudio.mutateAsync({ purpose: "hold_music", file });
      setDraft(null);
    } catch {
      // The block renders the failure inline.
    }
  };

  const resetToDefault = () =>
    updateMessage.mutate(
      { purpose: "hold_music", input: { mode: "default" } },
      {
        onSuccess: () => setDraft(null),
        onSettled: () => setConfirming(false),
      },
    );

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium text-ink">Hold music</p>
      <SegmentedControl
        aria-label="Hold music"
        options={MUSIC_OPTIONS}
        value={source}
        onValueChange={(value) => setDraft(value)}
        data-testid="phone-hold-music-source"
      />
      {source === "default" ? (
        <p
          className="text-sm text-annotation"
          data-testid="phone-hold-music-default"
        >
          A quiet instrumental loop.
          {saved === "custom" &&
            " Your uploaded track keeps playing until you switch back."}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {hold.music.audio ? (
            <p
              className="text-sm text-ink-body"
              data-testid="phone-hold-music-meta"
            >
              <span className="font-mono">{hold.music.audio.filename}</span>{" "}
              <span className="text-annotation">
                · uploaded {formatDateTime(hold.music.audio.uploadedAt)}
              </span>
            </p>
          ) : (
            <p
              className="text-sm text-annotation"
              data-testid="phone-hold-music-none"
            >
              MP3 or WAV, up to 5 MB. Until one lands, callers hear Brian's
              default track.
            </p>
          )}
          {hold.music.custom && (
            <MessageAudioPreview
              purpose="hold_music"
              testIdPrefix="phone-hold-music"
            />
          )}
          <AudioFilePicker
            label={
              picking
                ? "Uploading"
                : hold.music.audio
                  ? "Replace audio"
                  : "Upload audio"
            }
            busy={picking}
            onPick={(file) => void pick(file)}
            testIdPrefix="phone-hold-music"
          />
          {(refused || uploadAudio.isError) && (
            <FieldError data-testid="phone-hold-music-upload-error">
              {refused ?? uploadErrorNote(uploadAudio.error)}
            </FieldError>
          )}
        </div>
      )}
      {saved === "custom" && source === "default" && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => setConfirming(true)}
            data-testid="phone-hold-music-save"
          >
            Save
          </Button>
        </div>
      )}
      {updateMessage.isError && !confirming && (
        <FieldError data-testid="phone-hold-music-reset-error">
          Couldn't switch back to the default track. Try again.
        </FieldError>
      )}
      <ConfirmDialog
        open={confirming}
        data-testid={confirming ? "phone-hold-music-confirm" : undefined}
        title="Back to Brian's default track?"
        confirmLabel="Use default track"
        cancelLabel="Cancel"
        busy={updateMessage.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={resetToDefault}
        confirmTestId="phone-hold-music-confirm-confirm"
        cancelTestId="phone-hold-music-confirm-cancel"
      >
        <p className="text-sm text-ink-body">
          Your uploaded track is removed and callers on hold hear the default
          loop again.
        </p>
      </ConfirmDialog>
    </div>
  );
};

const minutes = (seconds: number): string => {
  const m = seconds / 60;
  return `${m} minute${m === 1 ? "" : "s"}`;
};

const MAX_HOLD_OPTIONS = HOLD_MAX_SECONDS_OPTIONS.map((seconds) => ({
  value: String(seconds),
  label: minutes(seconds),
}));

/** Reset to default only drafts brian's defaults; nothing changes until Save. */
const HoldTiming = ({ hold }: { hold: PhoneHoldSetup }) => {
  const update = useUpdateOutboundConfig();
  const [checkInDraft, setCheckInDraft] = useState<string | null>(null);
  const [maxDraft, setMaxDraft] = useState<number | null>(null);

  const checkInText = checkInDraft ?? String(hold.checkInSeconds);
  const checkIn = Number(checkInText);
  const maxSeconds = maxDraft ?? hold.maxSeconds;
  const checkInError =
    checkInText.trim() === "" || !isValidHoldCheckIn(checkIn)
      ? `Between ${HOLD_CHECK_IN_MIN_SECONDS} and ${HOLD_CHECK_IN_MAX_SECONDS} seconds.`
      : undefined;
  const dirty =
    checkIn !== hold.checkInSeconds || maxSeconds !== hold.maxSeconds;
  // An unlisted saved value still shows, so the select never reads as empty.
  const options = MAX_HOLD_OPTIONS.some((o) => o.value === String(maxSeconds))
    ? MAX_HOLD_OPTIONS
    : [
        ...MAX_HOLD_OPTIONS,
        { value: String(maxSeconds), label: minutes(maxSeconds) },
      ].sort((a, b) => Number(a.value) - Number(b.value));

  const save = () =>
    update.mutate(
      { holdCheckInSeconds: checkIn, holdMaxSeconds: maxSeconds },
      {
        onSuccess: () => {
          setCheckInDraft(null);
          setMaxDraft(null);
        },
      },
    );

  const reset = () => {
    setCheckInDraft(String(HOLD_CHECK_IN_DEFAULT_SECONDS));
    setMaxDraft(HOLD_MAX_DEFAULT_SECONDS);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Check in every"
          type="number"
          inputMode="numeric"
          min={HOLD_CHECK_IN_MIN_SECONDS}
          max={HOLD_CHECK_IN_MAX_SECONDS}
          step={5}
          value={checkInText}
          onChange={(e) => setCheckInDraft(e.target.value)}
          hint="Seconds between Brian's reminders that someone will be with them."
          error={checkInError}
          data-testid="phone-hold-check-in"
        />
        <Select
          label="Max hold time"
          options={options}
          value={String(maxSeconds)}
          onValueChange={(value) => setMaxDraft(Number(value))}
          hint="When the time is up, I tell the caller nobody could get to them and take a voicemail."
          data-testid="phone-hold-max"
        />
      </div>
      {update.isError && (
        <FieldError data-testid="phone-hold-timing-error">
          Couldn't save the hold timing. Try again.
        </FieldError>
      )}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={
            checkIn === HOLD_CHECK_IN_DEFAULT_SECONDS &&
            maxSeconds === HOLD_MAX_DEFAULT_SECONDS
          }
          onClick={reset}
          data-testid="phone-hold-timing-reset"
        >
          Reset to default
        </Button>
        <Button
          size="sm"
          disabled={!dirty || checkInError !== undefined}
          busy={update.isPending}
          onClick={save}
          data-testid="phone-hold-timing-save"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const HoldSummary = ({ hold }: { hold: PhoneHoldSetup }) => {
  const set = hold.messages.filter(isSet);
  const first = set[0];
  const track = hold.music.custom ? "your uploaded track" : "the default track";
  return (
    <p className="text-sm text-annotation" data-testid="phone-hold-summary">
      Callers on hold currently hear:{" "}
      {first ? (
        first.effectiveText !== null ? (
          <span className="text-ink-body">“{first.effectiveText}”</span>
        ) : (
          "your uploaded message"
        )
      ) : (
        "no message"
      )}
      {set.length > 1 &&
        ` and ${set.length - 1} more message${set.length - 1 === 1 ? "" : "s"}`}{" "}
      followed by {track}.
    </p>
  );
};
